import React, { useEffect, useRef, useState } from 'react';
import {
    useGetAiResponseHistoryQuery,
    useLazyGetAiResponseQuery,
    useLazyGetUserVoiceQuery,
    useSpeechToTextMutation,
    useStreamSpeechifyMutation
} from '../../api/speech.js';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import BaseButton from '../../components/form/BaseButton.jsx';
import RecordRTC from 'recordrtc';
import { useParams } from 'react-router-dom';
import BaseInput from '../../components/form/BaseInput.jsx';
import IconSend from '../../components/images/IconSend.jsx';
import { RadioButton } from 'primereact/radiobutton';
import { twMerge } from 'tailwind-merge';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import BaseCard from '../../components/misc/BaseCard.jsx';
import IconAi from '../../components/images/IconAi.jsx';
import IconPlayFull from '../../components/images/IconPlayFull.jsx';
import IconPause from '../../components/images/IconPause.jsx';
import IconReplay from '../../components/images/IconReplay.jsx';
import { TextWithLineBreaks } from '../../utils/markdown.jsx';

const PersonalAssistant = () => {
    const { id } = useParams();
    const [inputMode, setInputMode] = useState('record');
    const [reviewBeforeSubmit, setReviewBeforeSubmit] = useState(false);
    const [userInput, setUserInput] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const recorderRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [isCompatible, setIsCompatible] = useState(true);
    const [session, setSession] = useState('');
    const [messages, setMessages] = useState([]);
    const lastMessageRef = useRef(null);
    const [audioInstance, setAudioInstance] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [voiceId, setVoiceId] = useState('monica');

    const [getUserVoice] = useLazyGetUserVoiceQuery();
    const [aiResponse] = useLazyGetAiResponseQuery();
    const [triggerSTT] = useSpeechToTextMutation();
    const [streamSpeechify] = useStreamSpeechifyMutation();

    const { data: previousMessages } = useGetAiResponseHistoryQuery(id, {
        skip: !id
    });

    const handleStart = () => {
        if (!stream || !isCompatible || isRecording) return;

        const recorder = new RecordRTC(stream, {
            type: 'audio',
            mimeType: 'audio/wav',
            recorderType: RecordRTC.StereoAudioRecorder,
            desiredSampRate: 16000,
            numberOfAudioChannels: 1
        });

        recorder.startRecording();
        recorderRef.current = recorder;
        setIsRecording(true);
    };

    const handleUserMessage = async (text) => {
        setSendError(null);
        setIsSending(true);

        try {
            setMessages((prev) => [...prev, { role: 'user', message: text, timestamp: new Date().toISOString() }]);

            const aiRes = await aiResponse({
                input: text,
                session,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });

            const answer = aiRes?.data?.response?.data;

            if (!answer) throw new Error('No response from AI');
            setMessages((prev) => [
                ...prev,
                { role: 'assistant', message: answer, timestamp: new Date().toISOString() }
            ]);

            const { audioUrl } = await streamSpeechify({ input: answer, voice_id: voiceId }).unwrap();
            const audio = new Audio(audioUrl);

            if (audioInstance) {
                audioInstance.pause();
                URL.revokeObjectURL(audioInstance.src);
            }

            setAudioInstance(audio);
            audio.play();
            setIsPlaying(true);

            audio.onended = () => setIsPlaying(false);
        } catch (err) {
            console.error('Send message error:', err);
            setSendError(err?.data?.message || err.message);
        } finally {
            setIsSending(false);
        }
    };

    const handleStop = () => {
        if (!recorderRef.current || session === '' || !isRecording) return;

        setIsRecording(false);

        recorderRef.current.stopRecording(async () => {
            const blob = recorderRef.current.getBlob();

            if (inputMode === 'record') {
                try {
                    const sttRes = await triggerSTT({ data: { audio_file: blob } }).unwrap();

                    const userMessage = sttRes?.response?.text;
                    if (userMessage) {
                        if (reviewBeforeSubmit) {
                            setUserInput(userMessage);
                        } else {
                            await handleUserMessage(userMessage);
                        }
                    }
                } catch (err) {
                    console.error('Recording error:', err);
                }
            }

            recorderRef.current = null;
        });
    };

    const renderMessages = () => {
        return messages.map((msg, index) => (
            <BaseCard
                key={index}
                ref={index === messages.length - 1 ? lastMessageRef : null}
                className={`m-0 mb-6 p-4 bg-theme1-white w-[90%] ${msg.role !== 'assistant' ? 'self-end text-theme1-primary2' : 'bg-theme1-goldLight border-theme1-goldDark'}`}
            >
                {msg.role === 'assistant' && (
                    <IconAi className='stroke-theme1-gold flex-shrink-0 w-[30px] float-left mr-3 mt-1' />
                )}
                <strong className={`${msg.role === 'assistant' ? 'text-theme1-primary2' : ''}`}>
                    {msg.role === 'assistant' ? 'Assistant' : 'You'}:
                </strong>{' '}
                <span className={`${msg.role === 'assistant' ? 'text-theme1-primary2' : ''}`}>
                    <TextWithLineBreaks text={msg.message} />
                </span>
                <div className='text-xs text-gray-500 mt-1 text-right'>
                    {new Date(msg.timestamp).toLocaleTimeString()}
                </div>
                {audioInstance && index === messages.length - 1 && msg.role === 'assistant' && (
                    <div className='flex justify-center gap-4 items-center mt-4'>
                        <button
                            className='border-none bg-none'
                            onClick={() => {
                                audioInstance.pause();
                                setIsPlaying(false);
                            }}
                            disabled={!isPlaying}
                            title='Pause'
                        >
                            <IconPause className={`w-8  ${!isPlaying ? 'fill-theme1-text3' : 'fill-theme1-primary'}`} />
                        </button>
                        <button
                            className='border-none bg-none'
                            onClick={() => {
                                audioInstance.currentTime = 0;
                                audioInstance.play();
                                setIsPlaying(true);
                            }}
                            title='Replay'
                        >
                            <IconReplay className='w-9 fill-theme1-primary' />
                        </button>
                        <button
                            className='border-none bg-none'
                            onClick={() => {
                                if (audioInstance.currentTime === audioInstance.duration) {
                                    audioInstance.currentTime = 0;
                                }
                                audioInstance.play();
                                setIsPlaying(true);
                            }}
                            disabled={isPlaying}
                            title='Play'
                        >
                            <IconPlayFull
                                className={`w-[32px]  ${isPlaying ? 'fill-theme1-text3' : 'fill-theme1-primary'}`}
                            />
                        </button>
                    </div>
                )}
            </BaseCard>
        ));
    };

    useEffect(() => {
        getUserVoice()
            .unwrap()
            .then((res) => {
                if (res?.response?.voice) {
                    setVoiceId(res.response.voice.voice);
                }
            });
    }, [getUserVoice]);

    useEffect(() => {
        if (id && previousMessages) {
            const transformedMessages = previousMessages.response.assistantChats.map((chat) => ({
                role: chat.role,
                message: chat.message,
                timestamp: new Date(chat.created_at).toISOString()
            }));

            setMessages(transformedMessages);
        }
    }, [id, previousMessages]);

    useEffect(() => {
        if (id && !session) {
            setSession(id);
        } else if (!id && !session) {
            setSession(
                'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                    const r = (Math.random() * 16) | 0;
                    const v = c === 'x' ? r : (r & 0x3) | 0x8;
                    return v.toString(16);
                })
            );
        }
    }, [id, session]);

    useEffect(() => {
        if (lastMessageRef.current) {
            lastMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [messages]);

    useEffect(() => {
        if (!navigator?.mediaDevices?.getUserMedia) {
            console.warn('getUserMedia not supported');
            setIsCompatible(false);
        } else {
            navigator.mediaDevices
                .getUserMedia({ audio: true })
                .then(setStream)
                .catch((err) => {
                    console.error('Mic error:', err);
                    setIsCompatible(false);
                });
        }
    }, []);

    return (
        <AuthLayout title='Personal Assistant' backRoute='/personal-assistant-history' mainClassNames='flex flex-col'>
            <div className='max-w-3xl w-full h-full flex flex-col flex-grow justify-between mx-auto pt-4 py-0'>
                <div className='mt-auto flex flex-col'>
                    <div
                        className={`flex-grow flex flex-col overflow-auto ${inputMode === 'record' && reviewBeforeSubmit ? 'max-h-[570px]' : 'max-h-[625px]'} px-4`}
                    >
                        <div className='mt-auto flex flex-col relative pb-5'>
                            {messages.length > 0 ? (
                                renderMessages()
                            ) : (
                                <p className='text-gray-400'>Your messages will appear here...</p>
                            )}

                            {isSending && <ThreeDotsLoader className='absolute right-0 bottom-2.5' />}

                            {sendError && <div className='text-red-500 text-sm mt-2 text-right mb-2'>{sendError}</div>}
                        </div>
                    </div>

                    <div className='bg-theme1-tan rounded-t-4xl p-4 flex-col gap-2'>
                        <div className='flex items-center gap-4 mb-4'>
                            <div className='flex items-center gap-2'>
                                <RadioButton
                                    inputId='voiceMode'
                                    name='inputMode'
                                    value='record'
                                    checked={inputMode === 'record'}
                                    onChange={() => setInputMode('record')}
                                />
                                <label htmlFor='voiceMode' className='text-theme1-primary2'>
                                    Voice Mode
                                </label>
                            </div>
                            <div className='flex items-center gap-2'>
                                <RadioButton
                                    inputId='textMode'
                                    name='inputMode'
                                    value='text'
                                    checked={inputMode === 'text'}
                                    onChange={() => setInputMode('text')}
                                />
                                <label htmlFor='textMode' className='text-theme1-primary2'>
                                    Text Mode
                                </label>
                            </div>
                        </div>
                        <div className='flex items-center gap-4'>
                            {inputMode === 'record' && (
                                <div className='flex items-center gap-2 mb-2'>
                                    <input
                                        type='checkbox'
                                        checked={reviewBeforeSubmit}
                                        onChange={() => setReviewBeforeSubmit(!reviewBeforeSubmit)}
                                        id='reviewBeforeSubmit'
                                        className={twMerge(
                                            'h-5 w-5 appearance-none cursor-pointer rounded border border-theme1-primary flex-shrink-0',
                                            'checked:bg-theme1-tan relative',
                                            'before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
                                            'before:content-[""] before:w-4 before:h-4 before:bg-no-repeat before:bg-center before:opacity-0',
                                            "checked:before:opacity-100 checked:before:bg-[url('/src/resources/images/checkmark.svg')]"
                                        )}
                                    />
                                    <label htmlFor='reviewBeforeSubmit' className='text-theme1-primary2'>
                                        Review transcript before sending
                                    </label>
                                </div>
                            )}
                        </div>

                        {(inputMode === 'text' || (inputMode === 'record' && reviewBeforeSubmit)) && (
                            <form
                                className='flex items-center gap-2'
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    if (userInput.trim()) {
                                        handleUserMessage(userInput.trim());
                                        setUserInput('');
                                    }
                                }}
                            >
                                <div className='flex-grow px-5 border border-theme1-border1 rounded-4xl bg-white'>
                                    <BaseInput
                                        type='text'
                                        value={userInput}
                                        setValue={setUserInput}
                                        placeholder={`${inputMode === 'text' ? 'Type your message...' : 'Editable transcribed message...'}`}
                                        inputClassName='w-full pt-[12px] pb-[12px] h-[49px] max-h-[66px] focus:outline-none block bg-transparent mt-[2px] mb-[2px] px-0 flex-grow border-none'
                                        disabled={inputMode !== 'text' && !reviewBeforeSubmit}
                                    />
                                </div>

                                <button
                                    type='submit'
                                    className='btn rounded-4xl w-[55px] h-[55px] bg-theme1-primary text-white flex items-center justify-center'
                                    disabled={userInput.trim() === ''}
                                >
                                    <IconSend className='fill-white w-[30px] h-[30px] -ml-1' />
                                </button>
                            </form>
                        )}

                        {inputMode === 'record' && (
                            <div className='w-full mt-4'>
                                {isCompatible ? (
                                    <BaseButton
                                        className={`bg-theme1-primary text-theme1-white ${isRecording ? 'bg-red-500' : ''}`}
                                        onMouseDown={handleStart}
                                        onTouchStart={handleStart}
                                        onMouseUp={handleStop}
                                        onMouseLeave={handleStop}
                                        onTouchEnd={handleStop}
                                        onTouchCancel={handleStop}
                                    >
                                        {isRecording ? 'Recording...' : 'Press and Hold to Speak'}
                                    </BaseButton>
                                ) : (
                                    <p className='text-red-500'>Browser not compatible or mic blocked.</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
};

export default PersonalAssistant;
